/**
 * O cartão de cima da aba Lançamentos: o título, o Saldo Atual, os filtros, os
 * atalhos de período e as ações.
 *
 * Papel: reproduz `#lanc-filter-card` (l. 357–446): "Lançamentos" e "Saldo
 * Atual:" na primeira linha; Pesquisa, Tipo, Categoria, Status e Pagamento numa
 * grade `2fr 1fr 1fr 1fr 1fr`; e, numa linha só que não quebra, o período
 * "de – até", os atalhos de dia e de mês, "✕ Limpar" e, à direita, "Novo
 * Lançamento", "Importar", "Calculadora Petrella" e "Exportar XLSX".
 *
 * Como usar: os campos são controlados por quem monta, que recebe cada mudança
 * em `onMudar` e refaz a listagem; o cartão só desenha e calcula os atalhos com
 * o "hoje" de `hojeEmIso`. "✕ Limpar" chama `onLimpar`, porque além dos campos
 * ele limpa a seleção da tabela, que o cartão não conhece. Quem só tem `ver`
 * não vê "Novo Lançamento", as duas importações nem a calculadora;
 * "Exportar XLSX" fica para todos.
 *
 * As ações das importações e da calculadora são da parte 5: sem o manipulador,
 * o botão existe e não faz nada.
 */
import {
  type FormaPagamentoLancamento,
  ROTULOS_FORMA,
  type TipoLancamento,
} from '@concierge/shared';
import {
  IconCalculator,
  IconChevronLeft,
  IconChevronRight,
  IconFileImport,
  IconPlus,
  IconTableExport,
} from '@tabler/icons-react';

import { hojeEmIso } from '@/shared/lib/vencimento';
import {
  type FiltrosDosLancamentos,
  type Periodo,
  periodoDeHoje,
  periodoDoDia,
  periodoDoMes,
  periodoDoMesAtual,
} from '../filtros-dos-lancamentos';
import { formatarReais } from '../formato';
import { FORMAS_DO_FILTRO, OPCOES_DE_TIPO_DO_FILTRO } from '../rotulos';
import { BotaoDeAcao } from './kit/botao-de-acao';
import { Cartao } from './kit/cartao';
import {
  CLASSE_DA_DATA_DE_FILTRO,
  CLASSE_DO_CAMPO_DE_FILTRO,
  RotuloDeFiltro,
} from './kit/campo-de-filtro';

interface CartaoDeFiltrosProps {
  /** O Saldo Atual do caixa; nulo enquanto não chega, quando mostra "—". */
  saldoAtual: number | null;
  filtros: FiltrosDosLancamentos;
  /** Recebe só os campos que mudaram. */
  onMudar: (mudanca: Partial<FiltrosDosLancamentos>) => void;
  /** O "✕ Limpar". */
  onLimpar: () => void;
  /** As categorias do plano, em ordem alfabética, para o filtro "Categoria". */
  categorias: readonly string[];
  /** Falso para quem só tem `ver`. */
  podeEditar: boolean;
  /** O "Novo Lançamento". */
  onNovo: () => void;
  /** O "Exportar XLSX". */
  onExportar: () => void;
  /** O "Importar", que abre o modal de tipo (Folha, Guia DAE ou Boleto). */
  onImportar?: () => void;
  /** A "Calculadora Petrella", da parte 5. */
  onAbrirCalculadora?: () => void;
}

/** O filete vertical entre os grupos da segunda linha. */
function Filete() {
  return <div aria-hidden="true" className="h-4 w-px shrink-0 bg-emb-borda" />;
}

/**
 * O cartão de filtros.
 *
 * @param saldoAtual - O Saldo Atual, ou nulo.
 * @param filtros - O que está nos campos.
 * @param onMudar - Recebe os campos que mudaram.
 * @param onLimpar - O "✕ Limpar".
 * @param categorias - As opções do filtro "Categoria".
 * @param podeEditar - Mostra as ações que gravam.
 * @param onNovo - O "Novo Lançamento".
 * @param onExportar - O "Exportar XLSX".
 * @param onImportar - O "Importar", que abre o modal de tipo.
 * @param onAbrirCalculadora - A "Calculadora Petrella".
 * @returns O cartão.
 */
export function CartaoDeFiltros({
  saldoAtual,
  filtros,
  onMudar,
  onLimpar,
  categorias,
  podeEditar,
  onNovo,
  onExportar,
  onImportar,
  onAbrirCalculadora,
}: CartaoDeFiltrosProps) {
  /**
   * Aplica um atalho de período com o "hoje" de agora.
   *
   * @param atalho - O atalho, que recebe "de" e hoje.
   */
  function handleAtalho(atalho: (de: string, hoje: string) => Periodo) {
    onMudar(atalho(filtros.de, hojeEmIso()));
  }

  const saldoNegativo = saldoAtual !== null && Math.round(saldoAtual * 100) < 0;

  return (
    <Cartao className="mb-3 bg-background">
      <div className="px-[18px] pt-3.5 pb-3">
        <div className="mb-3.5 flex items-center justify-between">
          <div className="font-outfit text-[15px] font-light tracking-[0.08em] text-foreground uppercase">
            Lançamentos
          </div>
          <div className="flex items-center gap-2.5">
            <span className="text-[12px] text-texto-fraco">Saldo Atual:</span>
            <strong
              className={
                saldoNegativo ? 'text-[13px] text-perigo' : 'text-[13px] text-foreground'
              }
            >
              {saldoAtual === null ? '—' : formatarReais(saldoAtual)}
            </strong>
          </div>
        </div>

        <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] items-end gap-2">
          <div>
            <RotuloDeFiltro htmlFor="filtro-pesquisa">Pesquisa</RotuloDeFiltro>
            <input
              id="filtro-pesquisa"
              type="text"
              placeholder="Descrição, categoria..."
              // O `pesquisa` do `filtroDeLancamentosSchema` (packages/shared,
              // schemas/financas.ts) recusa mais de 200: a listagem voltaria 400.
              maxLength={200}
              value={filtros.pesquisa}
              onChange={(evento) => onMudar({ pesquisa: evento.target.value })}
              className={CLASSE_DO_CAMPO_DE_FILTRO}
            />
          </div>
          <div>
            <RotuloDeFiltro htmlFor="filtro-tipo">Tipo</RotuloDeFiltro>
            <select
              id="filtro-tipo"
              value={filtros.tipo}
              onChange={(evento) => onMudar({ tipo: tipoEscolhido(evento.target.value) })}
              className={CLASSE_DO_CAMPO_DE_FILTRO}
            >
              <option value="">Todos</option>
              {OPCOES_DE_TIPO_DO_FILTRO.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </div>
          <div>
            <RotuloDeFiltro htmlFor="filtro-categoria">Categoria</RotuloDeFiltro>
            <select
              id="filtro-categoria"
              value={filtros.categoria}
              onChange={(evento) => onMudar({ categoria: evento.target.value })}
              className={CLASSE_DO_CAMPO_DE_FILTRO}
            >
              <option value="">Todas</option>
              {categorias.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
          </div>
          <div>
            <RotuloDeFiltro htmlFor="filtro-status">Status</RotuloDeFiltro>
            <select
              id="filtro-status"
              value={filtros.status}
              onChange={(evento) => onMudar({ status: statusEscolhido(evento.target.value) })}
              className={CLASSE_DO_CAMPO_DE_FILTRO}
            >
              <option value="">Todos</option>
              <option value="pago">✓ Pago</option>
              <option value="pendente">⏳ Pendente</option>
            </select>
          </div>
          <div>
            <RotuloDeFiltro htmlFor="filtro-pagamento">Pagamento</RotuloDeFiltro>
            <select
              id="filtro-pagamento"
              value={filtros.forma}
              onChange={(evento) => onMudar({ forma: formaEscolhida(evento.target.value) })}
              className={CLASSE_DO_CAMPO_DE_FILTRO}
            >
              <option value="">Todos</option>
              {FORMAS_DO_FILTRO.map((forma) => (
                <option key={forma} value={forma}>
                  {ROTULOS_FORMA[forma]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-1.5 overflow-hidden">
          <div className="flex shrink-0 items-center gap-1">
            <input
              type="date"
              aria-label="De"
              value={filtros.de}
              onChange={(evento) => onMudar({ de: evento.target.value })}
              className={CLASSE_DA_DATA_DE_FILTRO}
            />
            <span className="text-[11px] text-texto-fraco">–</span>
            <input
              type="date"
              aria-label="Até"
              value={filtros.ate}
              onChange={(evento) => onMudar({ ate: evento.target.value })}
              className={CLASSE_DA_DATA_DE_FILTRO}
            />
          </div>
          <Filete />
          <BotaoDeAcao
            navegacao
            title="Dia anterior"
            className="px-2"
            onClick={() => handleAtalho((de, hoje) => periodoDoDia(de, hoje, -1))}
          >
            <IconChevronLeft size={11} aria-hidden="true" />
          </BotaoDeAcao>
          <BotaoDeAcao
            navegacao
            onClick={() => handleAtalho((_, hoje) => periodoDeHoje(hoje))}
          >
            Hoje
          </BotaoDeAcao>
          <BotaoDeAcao
            navegacao
            title="Próximo dia"
            className="px-2"
            onClick={() => handleAtalho((de, hoje) => periodoDoDia(de, hoje, 1))}
          >
            <IconChevronRight size={11} aria-hidden="true" />
          </BotaoDeAcao>
          <Filete />
          <BotaoDeAcao
            navegacao
            title="Mês anterior"
            className="px-2"
            onClick={() => handleAtalho((de, hoje) => periodoDoMes(de, hoje, -1))}
          >
            <IconChevronLeft size={11} aria-hidden="true" />
          </BotaoDeAcao>
          <BotaoDeAcao
            navegacao
            onClick={() => handleAtalho((_, hoje) => periodoDoMesAtual(hoje))}
          >
            Mês Atual
          </BotaoDeAcao>
          <BotaoDeAcao
            navegacao
            title="Próximo mês"
            className="px-2"
            onClick={() => handleAtalho((de, hoje) => periodoDoMes(de, hoje, 1))}
          >
            <IconChevronRight size={11} aria-hidden="true" />
          </BotaoDeAcao>
          <Filete />
          <BotaoDeAcao navegacao onClick={onLimpar}>
            ✕ Limpar
          </BotaoDeAcao>
          <div className="min-w-0 flex-1" />
          <div className="flex shrink-0 gap-1.5">
            {podeEditar && (
              <>
                <BotaoDeAcao onClick={onNovo}>
                  <IconPlus size={13} aria-hidden="true" /> Novo Lançamento
                </BotaoDeAcao>
                <BotaoDeAcao onClick={onImportar}>
                  <IconFileImport size={13} aria-hidden="true" /> Importar
                </BotaoDeAcao>
                <BotaoDeAcao onClick={onAbrirCalculadora}>
                  <IconCalculator size={13} aria-hidden="true" /> Calculadora Petrella
                </BotaoDeAcao>
              </>
            )}
            <BotaoDeAcao onClick={onExportar}>
              <IconTableExport size={13} aria-hidden="true" /> Exportar XLSX
            </BotaoDeAcao>
          </div>
        </div>
      </div>
    </Cartao>
  );
}

/**
 * O tipo escolhido no filtro, ou vazio para "Todos".
 *
 * @param valor - O `value` da opção.
 * @returns O tipo do contrato, ou vazio.
 */
function tipoEscolhido(valor: string): TipoLancamento | '' {
  return OPCOES_DE_TIPO_DO_FILTRO.find((opcao) => opcao.valor === valor)?.valor ?? '';
}

/**
 * A situação escolhida no filtro, ou vazio para "Todos".
 *
 * @param valor - O `value` da opção.
 * @returns `pago`, `pendente` ou vazio.
 */
function statusEscolhido(valor: string): 'pago' | 'pendente' | '' {
  return valor === 'pago' || valor === 'pendente' ? valor : '';
}

/**
 * A forma escolhida no filtro, ou vazio para "Todos".
 *
 * @param valor - O `value` da opção.
 * @returns A forma do contrato, ou vazio.
 */
function formaEscolhida(valor: string): FormaPagamentoLancamento | '' {
  return FORMAS_DO_FILTRO.find((forma) => forma === valor) ?? '';
}
